import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { FiCheck, FiCopy, FiX } from "react-icons/fi";
import {
  createPayoutIdempotencyKey,
  isPayoutApiMode,
  payoutApi,
} from "./api/payoutApiClient";
import {
  errorMessage,
  pickString,
  readSessionJson,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import { normalizeApiStatus } from "./api/payoutApiAdapters";
import PayoutCancelScheduledTransferModal from "./PayoutCancelScheduledTransferModal";

type ApprovalAction = "approve" | "reject";

const formatCountdown = (seconds: number) => {
  const safe = Math.max(0, seconds);
  const mm = String(Math.floor(safe / 60)).padStart(2, "0");
  const ss = String(safe % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const formatINR = (amount: number) =>
  `\u20B9 ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function FlowStep({
  label,
  complete,
}: {
  label: string;
  complete?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={
          "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold " +
          (complete
            ? "bg-[#1B163B] text-white"
            : "border border-[#D6DCEB] bg-white text-[#505E8E]")
        }
      >
        {complete ? <FiCheck /> : null}
      </span>
      <span className="text-sm font-semibold text-[#505E8E]">{label}</span>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid gap-4 border-b border-[#E0E5F2] px-5 py-4 md:grid-cols-[190px_minmax(0,1fr)]">
      <span className="text-sm font-semibold text-[#1B163B]">{label}</span>
      <div className="text-sm font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

function PayoutApprovalActionConfirm() {
  const { action: actionParam } = useParams();
  const [searchParams] = useSearchParams();
  const action: ApprovalAction = actionParam === "reject" ? "reject" : "approve";
  const transferId = searchParams.get("transferId") || "-";
  const rejectionReason = searchParams.get("reason") || "-";
  const isApproved = action === "approve";

  const sessionKey = `payoutApprovalActionResult:${transferId}`;
  const initialResult = useMemo(
    () => readSessionJson<Record<string, unknown> | null>(sessionKey, null),
    [sessionKey],
  );
  const [actionResult, setActionResult] = useState(initialResult);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [cancelMessage, setCancelMessage] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const paymentId = pickString(actionResult, [
    "payment_id",
    "paymentId",
    "id",
  ], transferId);
  const paymentStatus = normalizeApiStatus(
    pickString(actionResult, ["status", "status_label", "statusLabel"], ""),
    isApproved ? "Approved" : "Rejected",
  );
  const executeAt = pickString(actionResult, ["execute_at", "executeAt"]);
  const isScheduled = isApproved && paymentStatus === "Scheduled";
  const amountPaiseValue = Number(
    actionResult?.amount_paise ?? actionResult?.amountPaise ?? 0,
  );
  const cancelAmount =
    Number.isFinite(amountPaiseValue) && amountPaiseValue > 0
      ? formatINR(amountPaiseValue / 100)
      : "-";
  const cancelBeneficiary = pickString(
    actionResult,
    ["beneficiary_name", "beneficiaryName", "beneficiary"],
    "-",
  );
  const cancelScheduledFor = executeAt
    ? new Date(executeAt).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
        timeZoneName: "short",
      })
    : "-";

  useEffect(() => {
    if (!isScheduled || !actionResult) return;
    const fromApi =
      typeof actionResult.scheduled_remaining_seconds === "number"
        ? actionResult.scheduled_remaining_seconds
        : typeof actionResult.scheduledRemainingSeconds === "number"
          ? actionResult.scheduledRemainingSeconds
          : null;
    if (typeof fromApi === "number") {
      setRemainingSeconds(fromApi);
      return;
    }
    if (executeAt) {
      const ms = new Date(executeAt).getTime() - Date.now();
      setRemainingSeconds(Math.max(0, Math.ceil(ms / 1000)));
    }
  }, [actionResult, executeAt, isScheduled]);

  useEffect(() => {
    if (!isScheduled || remainingSeconds === null) return;
    if (remainingSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setRemainingSeconds((current) =>
        current === null ? null : Math.max(0, current - 1),
      );
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isScheduled, remainingSeconds]);

  useEffect(() => {
    if (!isPayoutApiMode() || !paymentId || !isScheduled) return;
    const poll = window.setInterval(async () => {
      try {
        const latest = await payoutApi.paymentDetails(paymentId);
        writeSessionJson(sessionKey, latest);
        setActionResult(latest);
        const status = normalizeApiStatus(pickString(latest, ["status"], ""));
        if (status !== "Scheduled") {
          setRemainingSeconds(0);
        } else if (typeof latest.scheduled_remaining_seconds === "number") {
          setRemainingSeconds(latest.scheduled_remaining_seconds);
        }
      } catch {
        // keep local countdown
      }
    }, 10_000);
    return () => window.clearInterval(poll);
  }, [isScheduled, paymentId, sessionKey]);

  const handleCancel = async () => {
    if (!paymentId || cancelling) return;
    setCancelling(true);
    setCancelMessage("");
    try {
      const cancelled = await payoutApi.cancelPayment(
        paymentId,
        { reason: "Cancelled during cooling-off window" },
        createPayoutIdempotencyKey("cancel-scheduled", paymentId),
      );
      writeSessionJson(sessionKey, cancelled);
      setActionResult(cancelled);
      setCancelMessage("Transfer cancelled before bank submission.");
      setRemainingSeconds(null);
      setCancelModalOpen(false);
    } catch (error) {
      setCancelMessage(errorMessage(error, "Unable to cancel this transfer."));
    } finally {
      setCancelling(false);
    }
  };

  const headline = !isApproved
    ? "Transfer Rejected Successfully!"
    : paymentStatus === "Cancelled"
      ? "Transfer Cancelled"
      : isScheduled
        ? "Transfer Scheduled"
        : "Transfer Approved Successfully!";

  const helper = !isApproved
    ? "Your rejection has been recorded and the transfer request has been stopped."
    : paymentStatus === "Cancelled"
      ? "This transfer was cancelled before bank submission."
      : isScheduled
        ? `Bank submission starts in ${formatCountdown(remainingSeconds ?? 0)}. You can cancel until then.`
        : "Your approval has been recorded and the transfer will move to the next workflow step.";

  const statusLabel = !isApproved
    ? "Rejected"
    : paymentStatus === "Cancelled"
      ? "Cancelled"
      : isScheduled
        ? "Scheduled"
        : paymentStatus === "Approved" || !actionResult
          ? "Approved"
          : paymentStatus;

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          {isApproved ? "Approval Submitted" : "Rejection Submitted"}
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          The transfer request has been updated successfully.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-10">
        <FlowStep label="Request Details" complete />
        <span className="h-px w-20 bg-[#D6DCEB]" />
        <FlowStep label="Verify" complete />
        <span className="h-px w-20 bg-[#D6DCEB]" />
        <FlowStep label="Confirm" complete />
      </div>

      <section className="max-w-[760px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 text-center new-shadow">
        <div
          className={
            "mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full text-4xl " +
            (!isApproved || paymentStatus === "Cancelled"
              ? "bg-[#FEE2E2] text-[#EF4444]"
              : isScheduled
                ? "bg-[#EDE9FE] text-[#6D28D9]"
                : "bg-[#DCFCE7] text-[#16A34A]")
          }
        >
          {isApproved && paymentStatus !== "Cancelled" ? <FiCheck /> : <FiX />}
        </div>
        <h2 className="text-[22px] font-semibold text-[#1B163B]">{headline}</h2>
        <p className="mt-3 text-sm font-medium text-[#505E8E]">{helper}</p>
        {isScheduled && remainingSeconds !== null ? (
          <p className="mt-4 text-3xl font-semibold text-[#6D28D9]">
            {formatCountdown(remainingSeconds)}
          </p>
        ) : null}
        {cancelMessage ? (
          <p className="mt-4 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#1B163B]">
            {cancelMessage}
          </p>
        ) : null}

        <div className="mt-7 overflow-hidden rounded-[8px] border border-[#E0E5F2] text-left">
          <DetailRow
            label="Transfer ID"
            value={
              <span className="flex items-center justify-between gap-3 break-all">
                {paymentId}
                <FiCopy className="shrink-0 text-[#505E8E]" />
              </span>
            }
          />
          {isScheduled && executeAt ? (
            <DetailRow
              label="Bank submission"
              value={new Date(executeAt).toLocaleString("en-IN")}
            />
          ) : null}
          {!isPayoutApiMode() || !actionResult ? (
            <>
              <DetailRow
                label="Beneficiary"
                value={
                  <span>
                    -
                    <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                      -
                    </span>
                  </span>
                }
              />
              <DetailRow label="Amount" value="-" />
              <DetailRow label="Requested By" value="-" />
            </>
          ) : null}
          <DetailRow
            label="Action Taken"
            value={isApproved ? "Approved" : "Rejected"}
          />
          {!isApproved && (
            <DetailRow label="Rejection Reason" value={rejectionReason} />
          )}
          <DetailRow
            label="Status"
            value={
              <span
                className={
                  "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                  (statusLabel === "Scheduled"
                    ? "bg-[#EDE9FE] text-[#6D28D9]"
                    : statusLabel === "Rejected" || statusLabel === "Cancelled"
                      ? "bg-[#FEE2E2] text-[#EF4444]"
                      : "bg-[#DCFCE7] text-[#16A34A]")
                }
              >
                {statusLabel}
              </span>
            }
          />
        </div>

        {isScheduled ? (
          <div className="mt-6">
            <button
              type="button"
              disabled={cancelling}
              onClick={() => setCancelModalOpen(true)}
              className="h-10 rounded-[6px] border border-[#FCA5A5] bg-white px-7 text-sm font-semibold text-[#DC2626] disabled:opacity-50"
            >
              {cancelling ? "Cancelling…" : "Cancel Transfer"}
            </button>
          </div>
        ) : (
          <div className="mt-6 rounded-[8px] border border-[#E0E5F2] bg-white p-5 text-left">
            <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">
              What's Next?
            </h3>
            <p className="mb-3 text-sm font-medium text-[#505E8E]">
              {"\u2022"} You can continue reviewing transfer requests in Pending
              Approval.
            </p>
            <p className="text-sm font-medium text-[#505E8E]">
              {"\u2022"} The requester will receive a status notification.
            </p>
          </div>
        )}
      </section>

      <div className="mt-8 flex max-w-[760px] items-center justify-between rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <Link
          to={isScheduled ? "/payouts/transfers" : "/payouts/transfers/pending-approval"}
          className="flex h-10 items-center rounded-[6px] border border-[#1B163B] px-7 text-sm font-semibold text-[#1B163B]"
        >
          {isScheduled ? "View Transfers" : "Back to Pending Approval"}
        </Link>
        <Link
          to="/payouts/transfers/pending-approval"
          className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
        >
          Review Another Request
        </Link>
      </div>

      {cancelModalOpen ? (
        <PayoutCancelScheduledTransferModal
          transactionId={paymentId || transferId}
          beneficiary={cancelBeneficiary}
          amount={cancelAmount}
          scheduledFor={cancelScheduledFor}
          confirming={cancelling}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={() => void handleCancel()}
        />
      ) : null}
    </div>
  );
}

export default PayoutApprovalActionConfirm;
